<template>
  <div class="login-features">

    <div class="lf-intro">
      <BrandLogo
        class="lf-logo"
        :height="34"
      />
      <p class="lf-tagline">{{ $t('subscriptions.list.title') }}</p>
      <p class="lf-targets-label">{{ $t('subscriptions.list.targetsLabel') }}</p>
      <div class="lf-targets">
        <span
          v-for="id in targets"
          :key="id"
          class="lf-target"
        >{{ $t('createBrief.templates.' + id + '.title') }}</span>
      </div>
    </div>

    <!-- Card 1 – Generate new article -->
    <div class="lf-card lf-card--generate">
      <div class="lf-card-top">
        <div class="lf-icon-wrap lf-icon-wrap--generate">
          <v-icon color="white" size="22">auto_awesome</v-icon>
        </div>
        <div>
          <div class="lf-card-title">{{ $t('subscriptions.list.generate.title') }}</div>
          <div class="lf-card-subtitle">{{ $t('subscriptions.list.generate.subtitle') }}</div>
        </div>
      </div>
      <ul class="lf-list">
        <li v-for="i in [1,2,3,4]" :key="'g'+i">
          <v-icon x-small class="lf-check" color="primary">check_circle</v-icon>
          <span>{{ $t('subscriptions.list.generate.items.' + i) }}</span>
        </li>
      </ul>
    </div>

    <!-- Card 2 – Analyze & improve from URL -->
    <div class="lf-card lf-card--analyze">
      <div class="lf-card-top">
        <div class="lf-icon-wrap lf-icon-wrap--analyze">
          <v-icon color="white" size="22">manage_search</v-icon>
        </div>
        <div>
          <div class="lf-card-title">{{ $t('subscriptions.list.analyze.title') }}</div>
          <div class="lf-card-subtitle">{{ $t('subscriptions.list.analyze.subtitle') }}</div>
        </div>
      </div>
      <ul class="lf-list">
        <li v-for="i in [1,2,3,4]" :key="'a'+i">
          <v-icon x-small class="lf-check" color="accent">check_circle</v-icon>
          <span>{{ $t('subscriptions.list.analyze.items.' + i) }}</span>
        </li>
      </ul>
    </div>

  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import BrandLogo from '@/components/BrandLogo.vue';
import {GEO_TEMPLATE_IDS} from '@/config/WritingBrief';

export default Vue.extend({
    name: 'LoginFeatures',
    components: {BrandLogo},
    computed: {
        targets(): string[] {
            return GEO_TEMPLATE_IDS.filter((id) => id !== 'universal');
        },
    },
});
</script>

<style lang="sass" scoped>
.login-features
  display: flex
  flex-direction: column
  gap: 16px
  padding: 8px 0

// ─── Intro / logo ─────────────────────────────────────────────────────────────
.lf-intro
  text-align: left
  margin-bottom: 8px

.lf-logo
  margin-bottom: 12px
  color: #0887dc

  ::v-deep .brand-logo
    // White logo on the light login column
    filter: brightness(0) saturate(100%) invert(37%) sepia(97%) saturate(632%) hue-rotate(183deg) brightness(95%) contrast(96%)

  .theme--dark &
    color: #fff

    ::v-deep .brand-logo
      filter: brightness(0) invert(1)

.lf-tagline
  font-size: 0.9rem
  line-height: 1.5
  opacity: 0.72
  margin: 0 0 12px
  max-width: 420px

.lf-targets-label
  font-size: 0.7rem
  font-weight: 700
  letter-spacing: 0.04em
  text-transform: uppercase
  opacity: 0.55
  margin: 0 0 8px

.lf-targets
  display: flex
  flex-wrap: wrap
  gap: 6px
  margin-bottom: 4px

.lf-target
  font-size: 0.72rem
  font-weight: 700
  line-height: 1.2
  padding: 5px 9px
  border-radius: 999px
  background: rgba(8, 135, 220, 0.1)
  color: #0566c3
  box-shadow: inset 0 0 0 1px rgba(8, 135, 220, 0.22)

.theme--dark .lf-target
  background: rgba(8, 135, 220, 0.22)
  color: #9ecfff
  box-shadow: inset 0 0 0 1px rgba(158, 207, 255, 0.25)

// ─── Feature cards ────────────────────────────────────────────────────────────
.lf-card
  background: #ffffff
  border-radius: 16px
  padding: 20px 22px
  box-shadow: 0 2px 12px rgba(58, 53, 65, 0.10)
  transition: box-shadow 0.2s ease

  .theme--dark &
    background: #2f3349
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.30)

  &:hover
    box-shadow: 0 6px 24px rgba(58, 53, 65, 0.16)

.lf-card-top
  display: flex
  align-items: flex-start
  gap: 14px
  margin-bottom: 14px

// ─── Icon circle ──────────────────────────────────────────────────────────────
.lf-icon-wrap
  flex-shrink: 0
  width: 44px
  height: 44px
  border-radius: 12px
  display: flex
  align-items: center
  justify-content: center

  &--generate
    background: linear-gradient(135deg, #0c98f5 0%, #0566c3 100%)

  &--analyze
    background: linear-gradient(135deg, var(--v-accent-base) 0%, #4d7724 100%)

// ─── Card text ────────────────────────────────────────────────────────────────
.lf-card-title
  font-size: 0.95rem
  font-weight: 700
  line-height: 1.3
  color: inherit

.lf-card-subtitle
  font-size: 0.78rem
  opacity: 0.62
  margin-top: 2px
  line-height: 1.35

// ─── Benefit list ─────────────────────────────────────────────────────────────
.lf-list
  list-style: none
  padding: 0
  margin: 0
  display: flex
  flex-direction: column
  gap: 8px

  li
    display: flex
    align-items: flex-start
    gap: 8px
    font-size: 0.83rem
    line-height: 1.45
    opacity: 0.85

.lf-check
  flex-shrink: 0
  margin-top: 2px
</style>
